import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, Trash2, Plus, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatDate, formatTime, formatPrice, imageUrl } from "@/lib/format";
import { StatusBadge, DemoBadge, RatingStars, EmptyState } from "@/components/common";
import { Switch } from "@/components/ui/switch";

export function useAdminList(url) {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get(url).then(({ data }) => setItems(data)), [url]);
  useEffect(() => { load(); }, [load]);
  return [items, load];
}

const run = async (fn, msg, reload) => {
  try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(formatApiError(e)); }
};

const Table = ({ head, children, testId }) => (
  <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid={testId}>
    <table className="w-full min-w-[720px] text-left text-sm">
      <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500"><tr>{head.map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr></thead>
      <tbody className="divide-y divide-slate-100">{children}</tbody>
    </table>
  </div>
);

export function Applications({ onChange }) {
  const [items, load] = useAdminList("/admin/applications");
  const reload = () => { load(); onChange(); };
  if (!items) return null;
  const pending = items.filter((a) => a.status === "pending");
  const decide = (a, action) => {
    const note = action === "reject" ? window.prompt("Reason for rejection (optional)") ?? "" : "";
    run(() => api.post(`/admin/providers/${a.provider_id}/${action}`, { note }), action === "approve" ? "Provider approved" : "Application rejected", reload);
  };
  return (
    <div className="space-y-4" data-testid="admin-applications">
      {pending.length === 0 && <EmptyState title="No pending applications" testId="applications-empty" />}
      {pending.map((a) => (
        <div key={a.id} className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 md:flex-row md:items-center" data-testid={`application-${a.provider_id}`}>
          <img src={imageUrl(a.provider?.profile_image)} alt="" className="h-16 w-16 rounded-xl bg-slate-100 object-cover" />
          <div className="flex-1">
            <div className="flex items-center gap-2"><p className="font-heading text-lg font-semibold">{a.business_name}</p>{a.provider?.is_demo && <DemoBadge />}</div>
            <p className="text-sm text-slate-500">{a.provider?.category_name} · {a.provider?.suburb}, {a.provider?.city} · {a.provider?.owner_name} · {a.provider?.email}</p>
            <p className="mt-1 line-clamp-2 text-sm text-slate-600">{a.provider?.description}</p>
            <p className="mt-1 text-xs text-slate-400">{a.services?.length || 0} services · submitted {formatDate(a.submitted_at.slice(0, 10))}</p>
          </div>
          <div className="flex gap-2">
            <Link to={`/provider/${a.provider_id}`} className="inline-flex h-10 items-center rounded-xl border border-slate-300 px-3 text-sm font-semibold" data-testid={`preview-${a.provider_id}`}>Preview</Link>
            <button data-testid={`approve-${a.provider_id}`} onClick={() => decide(a, "approve")} className="inline-flex h-10 items-center gap-1 rounded-xl bg-teal-600 px-3 text-sm font-semibold text-white"><Check className="h-4 w-4" />Approve</button>
            <button data-testid={`reject-${a.provider_id}`} onClick={() => decide(a, "reject")} className="inline-flex h-10 items-center gap-1 rounded-xl border border-rose-200 px-3 text-sm font-semibold text-rose-600"><X className="h-4 w-4" />Reject</button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function Providers() {
  const [items, load] = useAdminList("/admin/providers");
  if (!items) return null;
  return (
    <Table head={["Business", "Category", "Location", "Status", "Rating", "Featured", "Actions"]} testId="admin-providers">
      {items.map((p) => (
        <tr key={p.id} data-testid={`admin-provider-${p.id}`}>
          <td className="px-4 py-3"><Link to={`/provider/${p.id}`} className="font-semibold text-slate-900 hover:text-sky-700">{p.business_name}</Link> {p.is_demo && <DemoBadge />}</td>
          <td className="px-4 py-3">{p.category_name}</td>
          <td className="px-4 py-3">{p.suburb}, {p.city}</td>
          <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
          <td className="px-4 py-3">{p.rating_avg || "-"} ({p.review_count})</td>
          <td className="px-4 py-3"><Switch data-testid={`feature-toggle-${p.id}`} checked={!!p.featured} onCheckedChange={(v) => run(() => api.post(`/admin/providers/${p.id}/feature`, { value: v }), v ? "Featured" : "Unfeatured", load)} /></td>
          <td className="px-4 py-3">
            {["approved", "suspended"].includes(p.status) && (
              <button data-testid={`suspend-provider-${p.id}`} onClick={() => run(() => api.post(`/admin/providers/${p.id}/suspend`, { value: p.status === "approved" }), "Updated", load)} className="text-sm font-semibold text-rose-600">
                {p.status === "approved" ? "Suspend" : "Reinstate"}
              </button>
            )}
          </td>
        </tr>
      ))}
    </Table>
  );
}

export function Users() {
  const [items, load] = useAdminList("/admin/users");
  if (!items) return null;
  return (
    <Table head={["Name", "Email", "Role", "Status", "Joined", "Actions"]} testId="admin-users">
      {items.map((u) => (
        <tr key={u.id} data-testid={`admin-user-${u.id}`}>
          <td className="px-4 py-3 font-semibold">{u.name}</td>
          <td className="px-4 py-3">{u.email}</td>
          <td className="px-4 py-3 capitalize">{u.role}</td>
          <td className="px-4 py-3"><StatusBadge status={u.status} /></td>
          <td className="px-4 py-3">{formatDate(u.created_at.slice(0, 10), "d MMM yyyy")}</td>
          <td className="px-4 py-3">
            {u.role !== "admin" && (
              <button data-testid={`suspend-user-${u.id}`} onClick={() => run(() => api.post(`/admin/users/${u.id}/status`, { status: u.status === "active" ? "suspended" : "active" }), "User updated", load)} className={`text-sm font-semibold ${u.status === "active" ? "text-rose-600" : "text-teal-700"}`}>
                {u.status === "active" ? "Suspend" : "Activate"}
              </button>
            )}
          </td>
        </tr>
      ))}
    </Table>
  );
}

export function Bookings() {
  const [items] = useAdminList("/admin/bookings");
  if (!items) return null;
  return (
    <Table head={["Reference", "Provider", "Service", "Customer", "Date", "Price", "Status"]} testId="admin-bookings">
      {items.map((b) => (
        <tr key={b.id} data-testid={`admin-booking-${b.id}`}>
          <td className="px-4 py-3 font-mono text-xs">{b.reference}</td>
          <td className="px-4 py-3">{b.provider_name}</td>
          <td className="px-4 py-3">{b.service_name}</td>
          <td className="px-4 py-3">{b.customer_name}</td>
          <td className="px-4 py-3">{formatDate(b.date, "d MMM yyyy")} {formatTime(b.start_time)}</td>
          <td className="px-4 py-3">{formatPrice(b.price)}</td>
          <td className="px-4 py-3"><StatusBadge status={b.status} /></td>
        </tr>
      ))}
    </Table>
  );
}

export function Reviews({ onChange }) {
  const [items, load] = useAdminList("/admin/reviews");
  if (!items) return null;
  const remove = (r) => window.confirm("Remove this review?") && run(() => api.delete(`/admin/reviews/${r.id}`), "Review removed", () => { load(); onChange(); });
  return (
    <div className="grid gap-3 md:grid-cols-2" data-testid="admin-reviews">
      {items.map((r) => (
        <div key={r.id} className={`rounded-2xl border border-slate-200 bg-white p-5 ${r.removed ? "opacity-50" : ""}`} data-testid={`admin-review-${r.id}`}>
          <div className="flex items-center justify-between"><p className="font-semibold">{r.provider_name}</p><RatingStars value={r.rating} /></div>
          <p className="mt-1 text-xs text-slate-400">by {r.customer_name} · {r.service_name}</p>
          <p className="mt-2 text-sm text-slate-700">{r.comment}</p>
          {r.removed ? <p className="mt-3 text-xs font-semibold text-rose-600">Removed</p> : (
            <button data-testid={`remove-review-${r.id}`} onClick={() => remove(r)} className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-rose-600"><Trash2 className="h-4 w-4" />Remove</button>
          )}
        </div>
      ))}
    </div>
  );
}

export function Categories() {
  const [items, load] = useAdminList("/categories");
  const [form, setForm] = useState({ name: "", description: "", icon: "Sparkles", image: "" });
  const [editId, setEditId] = useState(null);
  if (!items) return null;
  const save = () => run(() => (editId ? api.put(`/admin/categories/${editId}`, form) : api.post("/admin/categories", form)), editId ? "Category updated" : "Category added", () => { setForm({ name: "", description: "", icon: "Sparkles", image: "" }); setEditId(null); load(); });
  const input = "h-11 rounded-xl border border-slate-200 px-3 text-sm";
  return (
    <div className="space-y-5" data-testid="admin-categories">
      <div className="flex flex-wrap gap-3 rounded-2xl border border-slate-200 bg-white p-5">
        <input data-testid="cat-name" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
        <input data-testid="cat-description" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${input} min-w-[200px] flex-1`} />
        <input data-testid="cat-image" placeholder="Image URL (optional)" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className={`${input} min-w-[200px] flex-1`} />
        <button data-testid="cat-save" onClick={save} className="inline-flex h-11 items-center gap-1 rounded-xl bg-sky-600 px-4 text-sm font-semibold text-white">{editId ? <><Pencil className="h-4 w-4" />Update</> : <><Plus className="h-4 w-4" />Add</>}</button>
      </div>
      <Table head={["Name", "Slug", "Providers", "Actions"]}>
        {items.map((c) => (
          <tr key={c.id} data-testid={`admin-category-${c.slug}`}>
            <td className="px-4 py-3 font-semibold">{c.name}</td>
            <td className="px-4 py-3 text-slate-500">{c.slug}</td>
            <td className="px-4 py-3">{c.provider_count}</td>
            <td className="space-x-3 px-4 py-3">
              <button data-testid={`cat-edit-${c.slug}`} onClick={() => { setEditId(c.id); setForm({ name: c.name, description: c.description, icon: c.icon, image: c.image }); }} className="text-sm font-semibold text-sky-700">Edit</button>
              <button data-testid={`cat-delete-${c.slug}`} onClick={() => window.confirm("Delete category?") && run(() => api.delete(`/admin/categories/${c.id}`), "Category deleted", load)} className="text-sm font-semibold text-rose-600">Delete</button>
            </td>
          </tr>
        ))}
      </Table>
    </div>
  );
}
